import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from "@angular/core";
import { DocumentModel } from "@models/documents/documentmodel";
import { MatTableDataSource } from "@angular/material/table";
import { EffTableColumnConfig } from "../eff-table/models/eff-table-column-config.model";
import { EffTableConfig, EffTableSelectionEnum } from "../eff-table/models/eff-table-config.model";
import { DroppedDocuments } from "../eff-drop-files-area/eff-drop-files-area.component";
import { OpenDocumentPopupReason } from "./models/add-documents-parameters.model";
import { DocumentManagerService } from "../services/document-manager.service";
import { PopupService } from "@app/shared/services/popup/popup.service";
import {
    EditDocumentPopup,
    OpenDocumentPopupReason as OpenReason,
} from "@app/shared/cerrix-documents/models/edit-document-popup.model";
import { MatDialog } from "@angular/material/dialog";
import { CerrixEditDocumentMonolithComponent } from "@app/shared/cerrix-documents/cerrix-edit-document/cerrix-edit-document-monolith/cerrix-edit-document-monolith.component";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { SelectOptionModel } from "@app/incident/components/src/lib/core/models/select-option.model";
import { TranslatePipe, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/controls/i18n/scope";
import { EffButtonWithConfirmationComponent } from "../eff-button-with-confirmation/eff-button-with-confirmation.component";
import { EffDocumentDetailsComponent } from "../document-views/eff-document-details/eff-document-details.component";
import { EffDropFilesAreaComponent } from "../eff-drop-files-area/eff-drop-files-area.component";
import { EffIconComponent } from "../eff-icon/eff-icon.component";
import { EffPopoverComponent } from "../eff-popover/eff-popover.component";
import { EffTableComponent, EffTableColumnDirective } from "../eff-table/eff-table.component";

@Component({
    selector: "document-manager-new",
    templateUrl: "./document-manager.component.html",
    styleUrls: ["./document-manager.component.scss"],
    imports: [
        TranslatePipe,
        EffButtonWithConfirmationComponent,
        EffDocumentDetailsComponent,
        EffDropFilesAreaComponent,
        EffIconComponent,
        EffPopoverComponent,
        EffTableComponent,
        EffTableColumnDirective,
    ],
    viewProviders: [provideTranslationScope(scope)]
})
export class DocumentManagerNewComponent implements OnChanges, OnInit {
    private _documentServiceManager = inject(DocumentManagerService);
    private _popupService = inject(PopupService);
    private _dialog = inject(MatDialog);
    private _sdService = inject(StandingdataDataService);

    @Input() documents: DocumentModel[] = [];
    @Input() canAdd = true;
    @Input() canEdit = true;
    @Input() canDelete = true;

    @Output() documentsChange = new EventEmitter<DocumentModel[]>();
    @Output() documentDeleted = new EventEmitter();

    dataSource: MatTableDataSource<DocumentModel> = new MatTableDataSource<DocumentModel>([]);
    tableConfig: EffTableConfig = {
        ExpandableRow: false,
        HideHeader: true,
        Selection: EffTableSelectionEnum.Single,
    };

    displayedColumns: Array<EffTableColumnConfig> = [
        { PropertyName: "Name", DisplayedNameKey: "effDocumentManager.columnName", Width: "100%", IsTemplate: true },
        { PropertyName: "Actions", DisplayedNameKey: "", IsTemplate: true },
    ];

    openPopupReason = OpenDocumentPopupReason;
    protected documentTypes: SelectOptionModel[] = [];

    constructor() {
        this._sdService.getDocumentTypes(null).subscribe((documentTypes) => {
            this.documentTypes = documentTypes.map((type) => {
                return { id: type.ID.toString(), name: type.Name };
            });
        });
    }

    ngOnInit() {
        this.refreshDocumentsTable();
        this.enableDisableCheckbox();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["documents"]) {
            this.refreshDocumentsTable();
        }

        if (changes["canAdd"]) {
            this.enableDisableCheckbox();
        }
    }

    enableDisableCheckbox() {
        if (!this.canAdd) {
            this.tableConfig.Selection = EffTableSelectionEnum.None;
        } else {
            this.tableConfig.Selection = EffTableSelectionEnum.Single;
        }
    }

    refreshDocumentsTable() {
        if (this.documents) {
            this.dataSource = new MatTableDataSource(this.documents.filter((doc) => !doc.Deleted));
        }
    }

    emitChange() {
        this.refreshDocumentsTable();
        this.documentsChange.emit(this.documents);
    }

    downloadDocument(doc: DocumentModel) {
        this._documentServiceManager.downloadDocument(doc);
    }

    onEditDocument(document: DocumentModel) {
        this._documentServiceManager.convertPropertiesToAbstract(document);
        const oldDoc = document;

        const dialogInput: EditDocumentPopup = {
            documents: [document],
            documentTypes: this.documentTypes,
            openReason: OpenReason.edit,
            canAddDocument: this.canAdd,
            canEditDocument: this.canEdit,
            showDelete: false,
        };

        let dialogRef = this._dialog.open(CerrixEditDocumentMonolithComponent, {
            width: "826px",
            data: dialogInput,
        });

        dialogRef.componentInstance.emitDownload.subscribe((document: DocumentModel) => {
            this.downloadDocument(document);
        });
        dialogRef.componentInstance.emitDelete.subscribe((document: DocumentModel) => {
            if (oldDoc.ID != null) {
                document.id = oldDoc.ID.toString();
            }
            this.deleteDocument(document);
        });

        dialogRef.afterClosed().subscribe((result) => {
            this._documentServiceManager.resetProperties(
                this.documents.find((doc) => doc.Guid === oldDoc.id),
                oldDoc
            );
            if (result?.length) {
                let parsedDocs = this.documents.map((d) => {
                    if (d.Guid === oldDoc.Guid) {
                        //undo null => "0" conversion by model conversion
                        if (result[0].documentTypeId === "0") {
                            result[0].documentTypeId = null;
                        }
                        return {
                            ...result[0],
                            Name: result[0].name,
                            Path: result[0].name,
                            Changed: true,
                            Description: result[0].description,
                            File: result[0].file,
                            CanDelete: true,
                            CanEdit: true,
                            Date: new Date(),
                            DocumentType: oldDoc.DocumentType,
                            DocumentTypeID: +result[0].documentTypeId,
                            FileIcon: result[0].fileIcon,
                            FileType: oldDoc.FileType,
                            IsNew: oldDoc.IsNew,
                            UserName: oldDoc.Username,
                            Version: d.Version++,
                        };
                    }
                    return d;
                });
                this.onDocumentsChange(parsedDocs);
            }
        });
    }

    public onDocumentsChange(documents: DocumentModel[]) {
        for (let i = 0; i < documents.length; i++) {
            if (documents[i].Changed === true) {
                for (const [key, value] of Object.entries(documents[i])) {
                    this.documents[i][key] = value;
                }
            }
        }
    }

    onFilesDropped(droppedDocuments: DroppedDocuments) {
        if (!this.canAdd) {
            return;
        }

        if (!droppedDocuments.IsBrowse) {
            this._documentServiceManager.addEditFiles(droppedDocuments.Documents, this.documents);
            this.emitChange();
        } else {
            this._documentServiceManager.openEditDocumentDialog(
                droppedDocuments.Documents,
                this.documents,
                OpenDocumentPopupReason.AddDocument,
                this.canAdd,
                this.canEdit,
                this.canDelete,
                (() => {
                    this.emitChange();
                }).bind(this)
            );
        }
    }

    deleteDocument(doc: DocumentModel) {
        this._documentServiceManager.deleteDocument(doc.Guid, this.documents);
        this.emitChange();
        this.documentDeleted.emit(doc);
    }

    openDocumentViewer(document: DocumentModel) {
        const url = "/api/documents/" + document.Guid + "/pdf";
        this._popupService.forceOpenInNewWindow(url);
    }

    get allowedFileExtensions() {
        return this._documentServiceManager?.documentConfig?.allowedFileExtensions;
    }
}
